import React from 'react'

export type SearchCategory = 'page' | 'product' | 'setting' | 'data'

export interface SearchItem {
  id: string
  title: { en: string; zh: string; zh_CN: string; jp: string; ko: string }
  description?: { en: string; zh: string; zh_CN: string; jp: string; ko: string }
  keywords: { en: string[]; zh: string[]; zh_CN: string[]; jp: string[]; ko: string[] }
  category: SearchCategory
  icon: React.ReactNode
  href: string
  external?: boolean
  requireOwner?: boolean
  requireLocalUser?: boolean
}

export const searchData: SearchItem[] = [
  // Pages
  {
    id: 'home',
    title: { en: 'Home', zh: '首頁', zh_CN: '首页', jp: 'ホーム', ko: '홈' },
    description: { en: 'Dashboard and overview', zh: '儀表板和概覽', zh_CN: '仪表板和概览', jp: 'ダッシュボードと概要', ko: '대시보드 및 개요' },
    keywords: {
      en: ['home', 'dashboard', 'overview', 'main'],
      zh: ['首頁', '主頁', '儀表板', '概覽'],
      zh_CN: ['首页', '主页', '仪表板', '概览'],
      jp: ['ホーム', 'ダッシュボード', '概要', 'メイン'],
      ko: ['홈', '대시보드', '개요', '메인']
    },
    category: 'page',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    href: '/'
  },
  {
    id: 'products',
    title: { en: 'Products', zh: '產品', zh_CN: '产品', jp: '製品', ko: '제품' },
    description: { en: 'Explore and manage products', zh: '探索和管理產品', zh_CN: '探索和管理产品', jp: '製品の探索と管理', ko: '제품 탐색 및 관리' },
    keywords: {
      en: ['products', 'apps', 'applications'],
      zh: ['產品', '應用', '應用程式'],
      zh_CN: ['产品', '应用', '应用程序'],
      jp: ['製品', 'アプリ', 'アプリケーション'],
      ko: ['제품', '앱', '애플리케이션']
    },
    category: 'page',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    href: '/products'
  },
  {
    id: 'plans',
    title: { en: 'Plans', zh: '方案', zh_CN: '方案', jp: 'プラン', ko: '플랜' },
    description: { en: 'View and manage plans', zh: '查看和管理方案', zh_CN: '查看和管理方案', jp: 'プランの表示と管理', ko: '플랜 보기 및 관리' },
    keywords: {
      en: ['plans', 'subscription', 'pricing'],
      zh: ['方案', '訂閱', '價格'],
      zh_CN: ['方案', '订阅', '价格'],
      jp: ['プラン', 'サブスクリプション', '価格'],
      ko: ['플랜', '구독', '가격']
    },
    category: 'page',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
      </svg>
    ),
    href: '/plans'
  },

  // Products are added at search time from the owner's list (src/config/productSearch.tsx).

  // Settings
  {
    id: 'settings',
    title: { en: 'Settings', zh: '設置', zh_CN: '设置', jp: '設定', ko: '설정' },
    description: { en: 'Manage account settings', zh: '管理帳戶設置', zh_CN: '管理账户设置', jp: 'アカウント設定の管理', ko: '계정 설정 관리' },
    keywords: {
      en: ['settings', 'preferences', 'configuration', 'options'],
      zh: ['設置', '設定', '偏好', '配置', '選項'],
      zh_CN: ['设置', '设定', '偏好', '配置', '选项'],
      jp: ['設定', '設定', '好み', '構成', 'オプション'],
      ko: ['설정', '환경설정', '선호도', '구성', '옵션']
    },
    category: 'setting',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    href: '/settings'
  },
  {
    id: 'products-settings',
    title: { en: 'Products settings', zh: '產品設定', zh_CN: '产品设置', jp: '製品設定', ko: '제품 설정' },
    description: { en: 'Choose which products this site shows', zh: '選擇此站台顯示哪些產品', zh_CN: '选择此站点显示哪些产品', jp: 'このサイトに表示する製品を選択', ko: '이 사이트에 표시할 제품 선택' },
    keywords: {
      en: ['products', 'menu', 'show', 'hide', 'visible'],
      zh: ['產品', '顯示', '隱藏'],
      zh_CN: ['产品', '显示', '隐藏'],
      jp: ['製品', '表示', '非表示'],
      ko: ['제품', '표시', '숨기기']
    },
    category: 'setting',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    href: '/settings/products',
    requireOwner: true
  },
  {
    id: 'language',
    title: { en: 'Language', zh: '語言', zh_CN: '语言', jp: '言語', ko: '언어' },
    description: { en: 'Change language preferences', zh: '更改語言偏好', zh_CN: '更改语言偏好', jp: '言語設定の変更', ko: '언어 설정 변경' },
    keywords: {
      en: ['language', 'locale', 'translation', 'english', 'chinese'],
      zh: ['語言', '語系', '翻譯', '英文', '中文'],
      zh_CN: ['语言', '语系', '翻译', '英文', '中文'],
      jp: ['言語', 'ロケール', '翻訳', '英語', '中国語'],
      ko: ['언어', '로케일', '번역', '영어', '중국어']
    },
    category: 'setting',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
      </svg>
    ),
    href: '/settings/language'
  },
  {
    id: 'profile',
    title: { en: 'Profile', zh: '個人資料', zh_CN: '个人资料', jp: 'プロフィール', ko: '프로필' },
    description: { en: 'Manage your profile information', zh: '管理您的個人資料', zh_CN: '管理您的个人资料', jp: 'プロフィール情報の管理', ko: '프로필 정보 관리' },
    keywords: {
      en: ['profile', 'account', 'user', 'personal info'],
      zh: ['個人資料', '帳戶', '用戶', '個人信息'],
      zh_CN: ['个人资料', '账户', '用户', '个人信息'],
      jp: ['プロフィール', 'アカウント', 'ユーザー', '個人情報'],
      ko: ['프로필', '계정', '사용자', '개인정보']
    },
    category: 'setting',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    href: '/settings/profile'
  },
  {
    id: 'password',
    title: { en: 'Change Password', zh: '修改密碼', zh_CN: '修改密码', jp: 'パスワード変更', ko: '비밀번호 변경' },
    description: { en: 'Update your password', zh: '更新您的密碼', zh_CN: '更新您的密码', jp: 'パスワードの更新', ko: '비밀번호 업데이트' },
    keywords: {
      en: ['password', 'change password', 'security', 'credentials'],
      zh: ['密碼', '修改密碼', '安全', '憑證'],
      zh_CN: ['密码', '修改密码', '安全', '凭据'],
      jp: ['パスワード', 'パスワード変更', 'セキュリティ', '認証情報'],
      ko: ['비밀번호', '비밀번호 변경', '보안', '인증정보']
    },
    category: 'setting',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
      </svg>
    ),
    href: '/settings/password',
    requireLocalUser: true
  },
  {
    id: 'users',
    title: { en: 'Users Management', zh: '用戶管理', zh_CN: '用户管理', jp: 'ユーザー管理', ko: '사용자 관리' },
    description: { en: 'Manage system users and permissions', zh: '管理系統用戶和權限', zh_CN: '管理系统用户和权限', jp: 'システムユーザーと権限の管理', ko: '시스템 사용자 및 권한 관리' },
    keywords: {
      en: ['users', 'user management', 'permissions', 'roles', 'access control'],
      zh: ['用戶', '用戶管理', '權限', '角色', '訪問控制'],
      zh_CN: ['用户', '用户管理', '权限', '角色', '访问控制'],
      jp: ['ユーザー', 'ユーザー管理', '権限', 'ロール', 'アクセス制御'],
      ko: ['사용자', '사용자 관리', '권한', '역할', '접근 제어']
    },
    category: 'setting',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    href: '/platform-users',
    requireOwner: true
  }
]
